'use client';
import type {
  ProductListV2,
  ProductMembership,
} from '@lib/types/api/ProductListV2';
import { createContext, useContext } from 'react';

import type { CommitListChangesParams } from './productListsHelpers';

export type { ProductInfo } from './productListsHelpers';

export type ProductListsContextValue = {
  personalLists: ProductListV2[];
  orgLists: ProductListV2[];
  isLoading: boolean;
  isProductInList: (productId: string) => boolean | null;
  toggleProductList: (product: { id: string; name: string }) => void;
  fetchProductMembership: (
    productId: string
  ) => Promise<ProductMembership | null>;
  fetchBatchProductMembership: (productIds: string[]) => Promise<void>;
  removeListFromMembershipCache: (listId: string) => Promise<void>;
  isProductListsEnabled: boolean;
  // Session and flags are known, so `isProductListsEnabled` and lists are real.
  isProductListsSettled: boolean;
  refreshPersonalLists: () => Promise<ProductListV2[] | undefined>;
  refreshProductMembership: (productId: string) => Promise<ProductMembership>;
  commitListChanges: (params: CommitListChangesParams) => Promise<void>;
};

export const ProductListsContext =
  createContext<ProductListsContextValue | null>(null);

const DEFAULT_CONTEXT: ProductListsContextValue = {
  personalLists: [],
  orgLists: [],
  isLoading: false,
  isProductInList: () => null,
  toggleProductList: () => undefined,
  fetchProductMembership: () => Promise.resolve(null),
  fetchBatchProductMembership: () => Promise.resolve(),
  removeListFromMembershipCache: () => Promise.resolve(),
  isProductListsEnabled: false,
  isProductListsSettled: true,
  refreshPersonalLists: () => Promise.resolve(undefined),
  refreshProductMembership: () =>
    Promise.reject(new Error('ProductListsProvider is not mounted')),
  commitListChanges: () =>
    Promise.reject(new Error('ProductListsProvider is not mounted')),
};

export const useProductLists = () => {
  const context = useContext(ProductListsContext);
  return context ?? DEFAULT_CONTEXT;
};
